import type { Company } from "../types";

export type CompanyInvoiceInfo = Pick<
  Company,
  | "invoice_address"
  | "invoice_state_name"
  | "invoice_state_code"
  | "bank_account_holder"
  | "bank_account_number"
  | "bank_ifsc_code"
>;

const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const GSTIN_PATTERN = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const BANK_ACCOUNT_PATTERN = /^\d{9,18}$/;
const STATE_CODE_PATTERN = /^\d{2}$/;

/** Labels of the mandatory invoice/bank fields that are still empty. GSTIN and branch stay optional. */
export function missingInvoiceInfo(company: CompanyInvoiceInfo): string[] {
  const required: [string | null | undefined, string][] = [
    [company.invoice_address, "address"],
    [company.invoice_state_name, "state name"],
    [company.invoice_state_code, "state code"],
    [company.bank_account_holder, "bank account holder"],
    [company.bank_account_number, "account number"],
    [company.bank_ifsc_code, "IFSC code"],
  ];
  return required.filter(([value]) => !value?.trim()).map(([, label]) => label);
}

/** Returns an error message for the first malformed value, or null. Empty optional values (GSTIN) pass. */
export function invoiceInfoFormatError(values: {
  gstin: string;
  stateCode: string;
  accountNumber: string;
  ifsc: string;
}): string | null {
  const gstin = values.gstin.trim().toUpperCase();
  if (gstin && !GSTIN_PATTERN.test(gstin)) {
    return "GSTIN must be 15 characters, e.g. 27ABCDE1234F1Z5 — leave it blank if you are not GST-registered.";
  }
  if (!STATE_CODE_PATTERN.test(values.stateCode.trim())) {
    return "State code must be the 2-digit GST state code, e.g. 27.";
  }
  if (!BANK_ACCOUNT_PATTERN.test(values.accountNumber.trim())) {
    return "Account number must be 9 to 18 digits.";
  }
  if (!IFSC_PATTERN.test(values.ifsc.trim().toUpperCase())) {
    return "IFSC code must be 11 characters, e.g. HDFC0001234.";
  }
  return null;
}
